import { Inject, Injectable, INJECTOR, Injector, TemplateRef, Type, ViewContainerRef } from '@angular/core';
import { PoprToast } from './toast/popr-toast';
import { PoprToastInjector } from './toast/popr-toast.injector';
import { PoprToastPackage } from './toast/popr-toast.package';
import { PoprPriority } from './popr-priority.enum';
import { PoprModalPackage } from './modal/popr-modal.package';
import { PoprModalInjector } from './modal/popr-modal.injector';
import { PoprModal } from './modal/popr-modal';
import { Popr } from './popr';
import { PriorityQueue } from './util/priority-queue';
import { delay, takeUntil, tap } from 'rxjs/operators';
import { PoprContainerMediator } from './popr-container.mediator';
import { PoprNgViewService, PoprViewService } from './popr-view.service';

/**
 * Priority token used to wrap {@link Popr}s for queueing
 */
export interface PoprToken {
  priority: number;
  popr: Popr;
}

/**
 * Service to generate and to maintain a priority queue of {@link Popr}s.
 *
 * @see {@link PoprModal}
 * @see {@link PoprToast}
 */
@Injectable({
  providedIn: 'root'
})
export class PoprService {
  private activeToken: PoprToken | undefined;
  private readonly queue = new PriorityQueue<PoprToken>((a, b) => {
    const pa: number = a.priority;
    const pb: number = b.priority;
    return pa < pb ? -1 : (pa > pb ? 1 : 0);
  });

  // view outlets
  private poprToastViewContainerRef: ViewContainerRef | undefined;
  private poprModalViewContainerRef: ViewContainerRef | undefined;

  constructor(
    @Inject('poprContainerMediator') private mediator: PoprContainerMediator,
    @Inject('poprToastTimeout') private toastTimeoutMillis: number,
    @Inject(INJECTOR) private injector: Injector
  ) {
    mediator.onPoprModalView$().subscribe(vcr => this.poprModalViewContainerRef = vcr);
    mediator.onPoprToastView$().subscribe(vcr => this.poprToastViewContainerRef = vcr);
  }

  /**
   * Return the number of {@link Popr}s in the queue
   */
  count(): number {
    return this.queue.size();
  }

  /**
   * Removes any {@link Popr}s from the queue
   */
  clear(): void {
    let token;
    while ((token = this.queue.pop())) token.popr?.close();
  }

  /**
   * Get a snapshot of the current queue
   */
  snapShot(): Readonly<PoprToken[]> {
    return this.queue.snapshot();
  }

  /**
   * @param poprPriority - used for queue ordering
   * @param popr - to be enqueued
   */
  protected enqueue(poprPriority: PoprPriority | number, popr: Popr): void {
    const priority = (typeof poprPriority === 'number') ? poprPriority : +PoprPriority[poprPriority];
    this.queue.push({ priority, popr });
    this.scan();
  }

  protected scan(): void {
    if (this.activeToken == null) {
      this.activeToken = this.queue.pop();
      this.activeToken?.popr.onClose$().subscribe(() => {
        this.activeToken = undefined;
        this.scan();
      });
      this.activeToken?.popr.open();
    }
  }

  /**
   * Creates and enqueue a modal {@link Popr} for future opening.
   *
   * @example
   * const modal = this.poprService.createModal(PoprPriority.INFO, MyModalComponent);
   * modal.component$.pipe(
   *   switchMap(comp => comp.emittedData$), // listen for data emission(s) from wrapped component
   *   finalize(() => modal.close()) // ensure modal is always closed
   * .subscribe(data => {
   *   // handle data emitted by component
   * });
   *
   * @param componentType - wrapped component to be the content of the modal
   * @param title - (optional) title to show on the modal
   *
   * @see {@link PoprModal}
   */
  createModal<T>(componentType: Type<T>, title?: string): PoprModal<T> {
    const modalPackage = new PoprModalPackage({ title });
    const modalInjector = Injector.create({
      parent: new PoprModalInjector(modalPackage, this.injector),
      providers: [
        { provide: 'dynamicComponentType', useValue: componentType },
        { provide: ViewContainerRef, useValue: this.poprModalViewContainerRef },
        { provide: PoprViewService, useClass: PoprNgViewService, deps: [ViewContainerRef] }
      ]
    });

    // build modal
    const modal = modalInjector.get(PoprModal);
    this.enqueue(PoprPriority.URGENT, modal);
    return modal;
  }

  /**
   * Creates and enqueue a toast {@link Popr}
   *
   * @example
   * // my.component.html
   * <div #toastTemplate>
   *   ...
   * </div>
   *
   * // my.component.ts
   * \@ViewChild('toastTemplate', {read: TemplateRef}) toastTemplate!: TemplateRef;
   *
   * showTemplateToastOnError(): void {
   *   this.poprService.createToast(PoprPriority.ERROR, this.toastTemplate, 'User Error');
   * }
   *
   * showTextToastOnSuccess(): void {
   *   this.poprService.createToast(PoprPriority.SUCCESS, 'everything worked', 'Success');
   * }
   *
   * @param priority - determines arrangement in the queue
   * @param content- wrapped content of the toast
   * @param title - (optional) title to show on the toast
   * @param buttonLabel - (optional) button label
   * @param buttonUrl - (optional) URL to navigate when button is pushed
   *
   * @see {@link PoprToast}
   */
  createToast(priority: PoprPriority, content: string | TemplateRef<any>, title?: string, buttonLabel?: string, buttonUrl?: string): PoprToast {
    const toastPackage = new PoprToastPackage({
      priority,
      title,
      button: {
        label: buttonLabel,
        url: buttonUrl
      }
    });
    const toastInjector = Injector.create({
      parent: new PoprToastInjector(toastPackage, this.injector),
      providers: [
        { provide: 'dynamicContent', useValue: content },
        { provide: ViewContainerRef, useValue: this.poprToastViewContainerRef },
        { provide: PoprViewService, useClass: PoprNgViewService, deps: [ViewContainerRef] }
      ]
    });

    // build toast
    const toast = toastInjector.get(PoprToast);
    toast.onOpen$().pipe(
      delay(this.toastTimeoutMillis),
      tap(() => toast.close()),
      takeUntil(toast.onClose$())
    ).subscribe();
    this.enqueue(priority, toast);
    return toast;
  }
}
